import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Renderer2 } from '@angular/core';

const SHADOW_CLASS = 'a-scroll-shadow';
const SCROLLED_CLASS = 'a-scroll-shadow--scrolled';

/**
 * Reveals the `.a-scroll-shadow` gradient at the top of a scroll region once it is scrolled down.
 *
 * Host it on the scroll container OR any ancestor of it — the directive listens for scroll in the
 * capture phase, so whatever element actually scrolls underneath (a native overflow container, or a
 * nested viewport such as ngx-scrollbar's) is discovered at runtime and decorated directly. That
 * self-discovery is deliberate: which element scrolls depends on layout that isn't knowable from the
 * template. Classes are toggled through the renderer, so an OnPush host needs no change detection.
 */
@Directive({
	selector: '[appScrollShadow]',
})
export class ScrollShadowDirective implements AfterViewInit, OnDestroy {
	// WeakSet: only tracks whether a scroller is already decorated, and lets detached scrollers
	// (the navigator swaps components on tab change) be garbage-collected.
	private readonly scrollers = new WeakSet<HTMLElement>();
	private readonly onScroll = (event: Event): void => this.decorate(event.target as HTMLElement | null);

	constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2, private zone: NgZone) {}

	ngAfterViewInit(): void {
		this.zone.runOutsideAngular(() => {
			// Capture phase so scroll from any descendant scroll container reaches us (scroll doesn't bubble).
			this.el.nativeElement.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
		});
	}

	ngOnDestroy(): void {
		this.el.nativeElement.removeEventListener('scroll', this.onScroll, { capture: true });
	}

	private decorate(scroller: HTMLElement | null): void {
		if (!scroller || scroller.nodeType !== Node.ELEMENT_NODE) return;

		if (!this.scrollers.has(scroller)) {
			this.scrollers.add(scroller);
			this.renderer.addClass(scroller, SHADOW_CLASS);
		}
		if (scroller.scrollTop > 0) {
			this.renderer.addClass(scroller, SCROLLED_CLASS);
		} else {
			this.renderer.removeClass(scroller, SCROLLED_CLASS);
		}
	}
}
